const streets = ['preflop', 'flop', 'turn', 'river'];

const getAllActionsForStreet = (gameState, street) => {
  return Object.entries(gameState.actions)
    .filter(([key]) => key.endsWith(`-${street}`))
    .flatMap(([key, actions]) => {
      const position = key.split('-')[0];
      return (Array.isArray(actions) ? actions : [actions]).map(action => ({
        position,
        ...action
      }));
    })
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
};

const getEligiblePlayers = (gameState, activePositions) => {
  const foldedPositions = new Set();
  
  Object.entries(gameState.actions).forEach(([key, actions]) => {
    const position = key.split('-')[0];
    const actionArray = Array.isArray(actions) ? actions : [actions];
    
    if (actionArray.some(action => action.action === 'fold')) {
      foldedPositions.add(position);
    }
  });
  
  return activePositions.filter(pos =>
    !foldedPositions.has(pos.name)
  );
};

const checkStreetComplete = (gameState, activePositions, street) => {
  const streetActions = getAllActionsForStreet(gameState, street);
  const eligiblePlayers = getEligiblePlayers(gameState, activePositions);
  
  if (eligiblePlayers.length <= 1) {
    return true; 
  }
  
  const playersWithStacks = eligiblePlayers.filter(player => 
    gameState.stacks[player.name] > 0
  );
  
  if (playersWithStacks.length <= 1) {
    return true;
  }
  
  const actionsByPosition = {};
  streetActions.forEach(action => {
    if (!actionsByPosition[action.position]) {
      actionsByPosition[action.position] = [];
    }
    actionsByPosition[action.position].push(action);
  });
  
  const lastRaise = streetActions
    .filter(a => (a.action === 'raise' || a.action === 'bet') && !a.isBlind)
    .pop();
  
  if (!lastRaise) {
    return playersWithStacks.every(player => 
      actionsByPosition[player.name] && 
      actionsByPosition[player.name].some(a => !a.isBlind)
    );
  }
  
  const raiseActionIndex = streetActions.indexOf(lastRaise);
  
  for (const player of playersWithStacks) {
    const playerActions = actionsByPosition[player.name] || [];
    
    if (player.name === lastRaise.position) {
      continue;
    }
    
    const hasActedAfterRaise = playerActions.some(action => {
      const actionIndex = streetActions.indexOf(action);
      return actionIndex > raiseActionIndex && !action.isBlind;
    });
    
    if (!hasActedAfterRaise) {
      return false;
    }
  }
  
  return true;
};

const determineCurrentStreet = (gameState, activePositions) => {
  let currentStreet = 'preflop';
  
  for (let streetIndex = 0; streetIndex < streets.length; streetIndex++) {
    const street = streets[streetIndex];
    currentStreet = street;
    
    const isStreetComplete = checkStreetComplete(gameState, activePositions, street);
    if (!isStreetComplete) {
      return street;
    }
    
    if (streetIndex < streets.length - 1) {
      continue;
    }
  }
  
  return currentStreet;
};

export const getUndoAction = (gameState) => {
  let lastActionKey = null;
  let lastActionTimestamp = 0;
  let lastActionIndex = -1;
  let lastAction = null;

  Object.entries(gameState.actions).forEach(([key, actions]) => {
    const actionArray = Array.isArray(actions) ? actions : [actions];
    
    actionArray.forEach((action, index) => {
      if (action.timestamp && action.timestamp > lastActionTimestamp) {
        lastActionTimestamp = action.timestamp;
        lastActionKey = key;
        lastActionIndex = index;
        lastAction = action;
      }
    });
  });

  if (!lastActionKey || !lastAction) {
    return null;
  }

  return {
    key: lastActionKey,
    index: lastActionIndex,
    action: lastAction,
    position: lastActionKey.split('-')[0]
  };
};

export const calculateGameIteration = (gameState, activePositions) => {
  
  const currentStreet = determineCurrentStreet(gameState, activePositions);
  const currentStreetActions = getAllActionsForStreet(gameState, currentStreet);
  const eligiblePlayers = getEligiblePlayers(gameState, activePositions);
  
  const playersWithStacks = eligiblePlayers.filter(player => 
    gameState.stacks[player.name] > 0
  );
  const handComplete = playersWithStacks.length <= 1;
  
  const currentBet = getCurrentBet(currentStreetActions);
  
  let actionOrder;
  if (currentStreet === 'preflop') {
    const bbIndex = activePositions.findIndex(p => p.name === 'BB');
    if (bbIndex !== -1) {
      actionOrder = [
        ...activePositions.slice(bbIndex + 1),
        ...activePositions.slice(0, bbIndex + 1)
      ];
    } else {
      actionOrder = activePositions;
    }
  } else {
    actionOrder = activePositions; 
  }
  
  const eligibleToAct = actionOrder.filter(pos =>
    eligiblePlayers.some(ep => ep.name === pos.name) &&
    gameState.stacks[pos.name] > 0
  );
  
  let nextToAct = null;
  
  if (!handComplete) {
    const lastRaise = currentStreetActions
      .filter(a => (a.action === 'raise' || a.action === 'bet') && !a.isBlind)
      .pop();
    
    if (lastRaise) {
      const raiserIndex = eligibleToAct.findIndex(p => p.name === lastRaise.position);
      const raiseActionIndex = currentStreetActions.indexOf(lastRaise);
      
      for (let i = 1; i < eligibleToAct.length; i++) {
        const checkIndex = (raiserIndex + i) % eligibleToAct.length;
        const checkPosition = eligibleToAct[checkIndex].name;
        
        const hasActedAfterRaise = currentStreetActions.some(action => 
          action.position === checkPosition &&
          !action.isBlind &&
          currentStreetActions.indexOf(action) > raiseActionIndex
        );
        
        if (!hasActedAfterRaise) {
          nextToAct = checkPosition;
          break;
        }
      }
    } else {
      for (const pos of eligibleToAct) {
        const hasActed = currentStreetActions.some(action => 
          action.position === pos.name && !action.isBlind
        );
        
        if (!hasActed) {
          nextToAct = pos.name;
          break;
        }
      }
    }
  }
  
  const streetComplete = !nextToAct;
  
  const potSize = Object.values(gameState.actions).reduce((total, actions) => {
    const actionArray = Array.isArray(actions) ? actions : [actions];
    return total + actionArray.reduce((streetTotal, action) => 
      streetTotal + (action.amount || 0), 0
    );
  }, 0);

  const undoInfo = getUndoAction(gameState);
  const actionRequirements = nextToAct
    ? getActionRequirements(gameState, currentStreet, nextToAct, currentBet)
    : null;
  
  return {
    currentStreet,
    nextToAct,
    currentBet,
    streetComplete,
    handComplete,
    activePositions: eligiblePlayers.map(p => p.name),
    foldedPositions: Array.from(new Set(activePositions.map(p => p.name).filter(name => 
      !eligiblePlayers.some(ep => ep.name === name)
    ))),
    actionsThisStreet: currentStreetActions.filter(a => !a.isBlind).length,
    potSize,
    lastAction: currentStreetActions.filter(a => !a.isBlind).pop() || null,
    allStreets: streets,
    streetIndex: streets.indexOf(currentStreet),
    playersWithStacks: playersWithStacks.length,
    canUndo: !!undoInfo,
    undoInfo: undoInfo,
    actionRequirements
  };
};

const getCurrentBet = (currentStreetActions) => {
  if (!currentStreetActions || currentStreetActions.length === 0) {
    return 0;
  }

  for (let i = currentStreetActions.length - 1; i >= 0; i -= 1) {
    const action = currentStreetActions[i];
    if (!action || action.isBlind) {
      continue;
    }

    if (action.action === 'raise' || action.action === 'bet' || action.action === 'posts') {
      return action.amount || 0;
    }

    if (action.action === 'check') {
      return 0;
    }
  }

  return 0;
};

const getMinRaise = (gameState, street, currentBet) => {
  const streetActions = getAllActionsForStreet(gameState, street)
    .filter(action =>
      (action.action === 'raise' || action.action === 'bet' || action.action === 'posts') &&
      !action.isBlind
    );

  if (streetActions.length < 2) {
    return currentBet;
  }

  const previousBet = streetActions[streetActions.length - 2].amount || 0;
  return Math.max(0, (currentBet * 2) - previousBet);
};

export const getActionRequirements = (gameState, street, position, currentBet) => {
  const playerContribution = Object.entries(gameState.actions)
    .filter(([key]) => key === `${position}-${street}`)
    .flatMap(([, actions]) => (Array.isArray(actions) ? actions : [actions]))
    .reduce((total, action) => total + (action.amount || 0), 0);

  const amountToCall = Math.max(0, currentBet - playerContribution);
  const canCheck = currentBet === 0 || amountToCall === 0;
  const minRaise = getMinRaise(gameState, street, currentBet);

  return {
    playerContribution,
    amountToCall,
    canCheck,
    minRaise
  };
};

export const undoLastAction = (gameState, setGameState) => {
  const undoInfo = getUndoAction(gameState);

  if (!undoInfo) {
    return false;
  }

  const { key: lastActionKey, index: lastActionIndex, action: lastAction, position } = undoInfo;
  
  setGameState(prev => {
    const existingActions = prev.actions[lastActionKey];
    const actionArray = Array.isArray(existingActions) ? existingActions : [existingActions];
    
    const newStacks = {
      ...prev.stacks,
      [position]: prev.stacks[position] + (lastAction.amount || 0)
    };

    let newActions = { ...prev.actions };
    if (actionArray.length === 1) {
      delete newActions[lastActionKey];
    } else {
      const updatedActions = actionArray.filter((_, index) => index !== lastActionIndex);
      newActions[lastActionKey] = updatedActions;
    }

    return {
      ...prev,
      actions: newActions,
      stacks: newStacks
    };
  });

  return true;
};

export const deriveCurrentAction = (gameState) => {
  const positions = Object.keys(gameState.stacks || {});
  const preflopActions = gameState.actions?.preflop || [];
  const nonBlindPreflopActions = preflopActions.filter((action) => !action.isBlind);
  const raises = preflopActions.filter((action) => action.action === 'raise' || action.action === 'bet');
  const currentBet = preflopActions.reduce((maxBet, action) => {
    if (action.action === 'posts' || action.action === 'raise' || action.action === 'bet') {
      return Math.max(maxBet, action.amount || 0);
    }
    return maxBet;
  }, 0);

  const playerIndex = positions.length > 0
    ? (nonBlindPreflopActions.length + 2) % positions.length
    : -1;
  const playerToAct = playerIndex >= 0 ? positions[playerIndex] : null;
  const playerContribution = preflopActions
    .filter((action) => action.position === playerToAct)
    .reduce((total, action) => total + (action.amount || 0), 0);

  const potSize = Object.values(gameState.actions || {}).reduce((streetTotal, streetActions) => {
    const actionsArray = Array.isArray(streetActions) ? streetActions : [];
    return streetTotal + actionsArray.reduce((sum, action) => sum + (action.amount || 0), 0);
  }, 0);

  const amountToCall = Math.max(0, currentBet - playerContribution);

  return {
    street: 'preflop',
    playerToAct,
    potSize,
    amountToCall,
    raiseIndex: raises.length,
    validActions: playerToAct
      ? ['fold', amountToCall > 0 ? 'call' : 'check', 'raise']
      : [],
  };
};
